import { Badge, type BadgeVariant, IconButton, PageShell, Sidebar } from '@nrg/design-system';
import { type MouseEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LuLogOut } from 'react-icons/lu';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { isForbiddenError } from '@/api/errors';
import { useCapabilities } from '@/auth/capabilities';
import { type AccessLevel, resolveAccessLevel, useIamOrganisations, useIamUser } from '@/auth/iam';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { SidebarBrandLogo } from '@/branding/SidebarBrandLogo';
import { LoadingShell } from '@/components/LoadingShell';
import { AppTopBar } from '@/modules/topbar';
import Forbidden from '@/pages/Forbidden';
import { useOrganizationsStore } from '@/stores';

import { NAV_ITEMS } from './nav';
import { resolveActiveNavId } from './nav-active';

const ACCESS_VARIANT: Record<AccessLevel['level'], BadgeVariant> = {
  domain: 'info',
  tenant: 'neutral',
  organisation: 'healthy',
};

/**
 * The design system's Sidebar renders `<a href>` items and does not call
 * `preventDefault` on click, so without interception every nav click would
 * trigger a full-page reload. This delegates the bubbled click to react-router
 * while preserving modifier-key, middle-click and `target="_blank"` semantics.
 */
function shouldInterceptAnchorClick(
  event: MouseEvent<HTMLElement>,
  anchor: HTMLAnchorElement,
): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.origin !== window.location.origin) return false;
  return true;
}

/**
 * The caller's IAM scope, shown in the sidebar: the access level (domain /
 * tenant / organisation) plus the tenant or organisation it is bound to —
 * the same classification the invoices page shows.
 */
function AccessLevelBadge({ access }: { access: AccessLevel }) {
  const { t } = useTranslation();
  const detail =
    access.level === 'tenant'
      ? (access.tenantName ?? access.tenantId)
      : access.level === 'organisation'
        ? (access.organisationName ?? access.organisationId)
        : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <Badge variant={ACCESS_VARIANT[access.level]}>{t(`sidebar.access.${access.level}`)}</Badge>
      {detail && (
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={detail}
        >
          {detail}
        </span>
      )}
    </div>
  );
}

export default function AuthenticatedLayout() {
  const { t } = useTranslation();
  const { user, signOut } = useCurrentUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Cognito identity (shown on the lockout page).
  const signedInEmail = user?.signInDetails?.loginId ?? user?.username;

  const activeNavId = resolveActiveNavId(pathname, NAV_ITEMS);

  // IAM is the identity authority: it classifies the caller as domain / tenant /
  // organisation level and yields the organisations they may act on. The list feeds
  // the org store, which is the source of truth for "currently selected org"
  // everywhere downstream (split-billing, QR).
  const { data: iamUser, isPending, isError, error } = useIamUser();
  const access = iamUser ? resolveAccessLevel(iamUser.user) : undefined;

  // Domain-only items (ODA — its data is global, so the backend 403s everyone
  // else) are hidden from non-domain callers; a section left empty disappears.
  // Feature-gated items (invoices) are hidden the same way, from the backend's
  // `/api/me/capabilities` answer — absent until it resolves, so a gated item
  // appears once the grant is confirmed rather than flashing and vanishing.
  const isDomain = access?.level === 'domain';
  const { data: capabilities } = useCapabilities();
  const features = capabilities?.features;
  const navSections = useMemo(
    () =>
      NAV_ITEMS.map((section) => ({
        label: t(section.labelKey),
        items: section.items
          .filter((item) => !item.domainOnly || isDomain)
          .filter(
            (item) => !item.requiresFeature || (features?.includes(item.requiresFeature) ?? false),
          )
          .map((item) => ({ ...item, label: t(item.labelKey) })),
      })).filter((section) => section.items.length > 0),
    [t, isDomain, features],
  );
  const { data: iamOrganisations } = useIamOrganisations(access);

  // No org picker in the shell — pages are scope-filtered server-side. The IAM
  // organisations still feed the store: it auto-selects the first one, which the
  // org-scoped pages (split-billing, QR, helpdesk creation) read.
  const setOrganizations = useOrganizationsStore((s) => s.setOrganizations);

  useEffect(() => {
    if (iamOrganisations) setOrganizations(iamOrganisations);
  }, [iamOrganisations, setOrganizations]);

  // Just ask Amplify to end the session. Clearing the cache and the stores happens in
  // `AuthRouteSwitch`, once `authStatus` has actually left `authenticated` and this
  // tree has unmounted — doing it here instead refetched every active query on the
  // outgoing user's still-valid token and refilled the cache it was meant to empty.
  const handleSignOut = () => {
    signOut();
  };

  const handleNavBodyClick = (event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement | null)?.closest(
      'a[href]',
    ) as HTMLAnchorElement | null;
    if (!anchor) return;
    if (!shouldInterceptAnchorClick(event, anchor)) return;
    event.preventDefault();
    navigate(`${anchor.pathname}${anchor.search}${anchor.hash}`);
  };

  // Authorization gate: while the IAM identity bootstraps, show a neutral loading
  // shell; if IAM rejects this user (401/403 from /rest/current-user), replace the
  // whole shell with the lockout page so no child route can mount. Other errors
  // (IAM down) fall through to the shell — pages surface their own errors.
  if (isPending) {
    return <LoadingShell />;
  }

  if (isError && isForbiddenError(error)) {
    return (
      <Forbidden onSignOut={handleSignOut} {...(signedInEmail ? { email: signedInEmail } : {})} />
    );
  }

  return (
    <PageShell
      constrainContent={false}
      sidebar={
        <Sidebar ariaLabel={t('appName')}>
          <SidebarBrandLogo access={access} />

          <Sidebar.Header>{access && <AccessLevelBadge access={access} />}</Sidebar.Header>

          <Sidebar.Body onClick={handleNavBodyClick}>
            <Sidebar.Nav
              sections={navSections}
              {...(activeNavId !== undefined && { activeId: activeNavId })}
            />
          </Sidebar.Body>

          <Sidebar.User
            name={user?.signInDetails?.loginId ?? user?.username ?? ''}
            subtitle="User"
            avatar="U"
            trailing={
              <IconButton
                label="Logout"
                variant="secondary"
                onClick={handleSignOut}
                aria-label={t('logout')}
              >
                <LuLogOut />
              </IconButton>
            }
          />
        </Sidebar>
      }
      topBar={<AppTopBar />}
    >
      <Outlet />
    </PageShell>
  );
}
